import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, X, AlertTriangle } from "lucide-react";
import { Button, Pill } from "./common/Primitives";
import api, { formatApiError } from "../lib/api";

/**
 * Reusable rejection reason modal.
 * Props:
 *   open, onClose  — control the modal
 *   context        — "interest" | "deal_room"
 *   targetId       — interest_id or deal_room_id
 *   startupId      — startup being rejected
 *   startupName    — for the header
 *   onSubmitted    — callback(rejectionDoc)
 */
export default function RejectionReasonModal({ open, onClose, context, targetId, startupId, startupName, onSubmitted }) {
  const [catalog, setCatalog] = useState([]);
  const [primary, setPrimary] = useState("");
  const [secondary, setSecondary] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/rejection-reasons/catalog").then((r) => setCatalog(r.data?.items || [])).catch(() => {});
    setPrimary(""); setSecondary([]); setNote("");
  }, [open]);

  if (!open) return null;

  const toggleSecondary = (key) => {
    if (key === primary) return;
    setSecondary((prev) => {
      if (prev.includes(key)) return prev.filter((x) => x !== key);
      if (prev.length >= 2) return prev; // cap at 2
      return [...prev, key];
    });
  };

  const submit = async () => {
    if (!primary) { toast.error("Pick a primary reason"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/investor/rejection-reason", {
        context, target_id: targetId, startup_id: startupId,
        primary_reason: primary,
        secondary_reasons: secondary,
        note: note.trim() || null,
      });
      toast.success("Feedback recorded — thank you");
      onSubmitted?.(data);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose} data-testid="rejection-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-navy/[0.06] flex items-center justify-between">
          <div>
            <div className="overline flex items-center gap-1"><AlertTriangle size={12} /> WHY ARE YOU PASSING?</div>
            <div className="font-heading text-ink text-lg mt-1">{startupName || "This opportunity"}</div>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink" data-testid="rejection-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1">
          <p className="text-xs text-ink-soft mb-4">
            Your feedback stays anonymised for the founder — we only surface aggregated patterns like
            &ldquo;3 investors passed due to valuation concern.&rdquo; The admin team sees full detail to
            improve future matches.
          </p>

          <div className="mb-4">
            <div className="text-[10px] tracking-widest uppercase text-ink-muted mb-2">Primary reason (required)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {catalog.map((r) => (
                <label
                  key={r.key}
                  className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer transition-colors ${
                    primary === r.key ? "border-navy bg-navy/[0.06]" : "border-navy/10 hover:bg-navy/[0.02]"
                  }`}
                  data-testid={`rej-primary-${r.key}`}
                >
                  <input
                    type="radio"
                    name="primary"
                    className="text-navy"
                    checked={primary === r.key}
                    onChange={() => setPrimary(r.key)}
                  />
                  <span className="text-sm text-ink">{r.label}</span>
                </label>
              ))}
            </div>
          </div>

          {primary && (
            <div className="mb-4">
              <div className="text-[10px] tracking-widest uppercase text-ink-muted mb-2">
                Secondary reasons (up to 2)
              </div>
              <div className="flex flex-wrap gap-1.5">
                {catalog.filter((r) => r.key !== primary).map((r) => {
                  const on = secondary.includes(r.key);
                  const disabled = !on && secondary.length >= 2;
                  return (
                    <button
                      key={r.key}
                      type="button"
                      disabled={disabled}
                      onClick={() => toggleSecondary(r.key)}
                      className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                        on ? "border-gold bg-gold/10 text-navy" : disabled ? "border-navy/5 text-ink-faint cursor-not-allowed" : "border-navy/15 text-ink-soft hover:bg-navy/[0.02]"
                      }`}
                      data-testid={`rej-secondary-${r.key}`}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mb-2">
            <div className="text-[10px] tracking-widest uppercase text-ink-muted mb-2">Note (optional)</div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Anything specific the founder or team should hear? Kept internal."
              className="w-full text-sm bg-white border border-navy/15 rounded px-3 py-2 focus:border-gold outline-none"
              data-testid="rejection-note"
            />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-navy/[0.06] flex items-center justify-between">
          <Pill tone="muted">
            {primary ? `${1 + secondary.length}/3 reason${secondary.length ? "s" : ""}` : "0/3 reasons"}
          </Pill>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="primary" onClick={submit} disabled={saving || !primary} data-testid="rejection-submit">
              {saving ? <Loader2 className="animate-spin inline mr-1" size={12} /> : null}
              Submit feedback
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
